/**
 * Button — shadcn/ui primitive (TruSpace adoption, 2026-05-22).
 *
 * This file is the standard shadcn `Button` template, themed against
 * the TruSpace token system. The `default` variant uses the TruSpace
 * teal (`--color-primary`) and is the ONE primary CTA of a page;
 * `tertiary` is the SSI orange for a nested surface's one high-emphasis
 * action; everything else uses the neutral semantic tokens.
 *
 * `disabled` is a STATE on any variant, not a variant of its own — and a
 * grey `secondary` button is fully interactive, never a way to signal
 * unavailability (`component-visual.mdc` §1.2).
 *
 * Notes:
 *   - `forwardRef` is intentionally omitted — React 19 forwards refs
 *     through function components natively (per components.mdc §10).
 *   - cva drives the variant + size matrix.
 *   - `asChild` (Radix Slot) lets you render the styles on a child
 *     element: `<Button asChild><Link to="/x">Go</Link></Button>`.
 *     The single biggest ergonomic win over hand-rolled buttons.
 *
 * To add another variant (e.g. a "warning" amber variant), edit the
 * `buttonVariants` object below — never recolor at the call site.
 *
 * @see https://ui.shadcn.com/docs/components/button — upstream source.
 */
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  // Base styles — apply to every variant. The focus ring is the brand teal
  // `--color-ring`; `ring-offset-2` is load-bearing rather than decorative,
  // since without the gap a teal ring on the teal-filled `default` button is
  // the same hue as the fill and the focus state disappears (a11y.mdc §2).
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        // Hover is a token, not `/80`: the refresh moved `--color-secondary` to
        // a mid-grey, and an opacity step over a light page BRIGHTENS that fill,
        // which reads as the button going disabled on hover — the exact
        // confusion `component-visual.mdc` §1.2 exists to prevent. The token
        // darkens in light mode and lightens in dark, so hover always moves
        // away from the page background.
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary-hover',
        // Tertiary is the ONE high-emphasis action of a NESTED surface — a
        // panel, drawer, sub-page toolbar, or tab-level action region inside a
        // page whose own teal CTA must stay unique. Chosen by surface nesting,
        // never by route depth, and never applied as a call-site className
        // (`component-visual.mdc` §1.1, `design-tokens.mdc` §1.5). The
        // foreground is SSI Black, not white: orange with white is 2.28:1.
        tertiary:
          'bg-tertiary text-tertiary-foreground hover:bg-tertiary/90',
        ghost:
          'hover:bg-accent hover:text-accent-foreground',
        link:
          'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /**
     * If true, the styles are applied to the child element instead of
     * a `<button>` wrapper. Useful for rendering an anchor / Link
     * styled as a button. Implemented via `@radix-ui/react-slot`.
     */
    asChild?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

// `buttonVariants` is the cva-generated class composer. We export it
// alongside `Button` so consumers can apply Button styles to
// non-button elements (`buttonVariants({ variant: 'outline' })` as a
// `className`). The upstream shadcn template does the same. The
// eslint-disable is required because `react-refresh/only-export-components`
// can't reason about non-component side-exports during Vite HMR;
// production builds are unaffected.
// eslint-disable-next-line react-refresh/only-export-components
export { buttonVariants };
